import { MaxUint256 } from '@ethersproject/constants'
import { useTranslation } from '@pancakeswap/localization'
import { bscTestnetTokens, bscTokens } from '@pancakeswap/tokens'
import {
  AddIcon,
  ArrowForwardIcon,
  BalanceInput,
  Button,
  Flex,
  HelpIcon,
  MinusIcon,
  Modal,
  Skeleton,
  Text,
  Ticket,
  useToast,
  useTooltip,
} from '@pancakeswap/uikit'
import { useWeb3React } from '@pancakeswap/wagmi'
import BigNumber from 'bignumber.js'
import ApproveConfirmButtons, { ButtonArrangement } from 'components/ApproveConfirmButtons'
import ConnectWalletButton from 'components/ConnectWalletButton'
import { ToastDescriptionWithTx } from 'components/Toast'
import { FetchStatus } from 'config/constants/types'
import useApproveConfirmTransaction from 'hooks/useApproveConfirmTransaction'
import { useCake, useLotteryV2Contract, useNewLotteryV2Contract } from 'hooks/useContract'
import useTheme from 'hooks/useTheme'
import useTokenBalance, { useGetBnbBalance } from 'hooks/useTokenBalance'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useAppDispatch } from 'state'
import { useEthPriceBusd } from 'state/farms/hooks'
import { fetchUserTicketsAndLotteries } from 'state/lottery'
import { useLottery } from 'state/lottery/hooks'
import styled from 'styled-components'
import { BIG_ZERO } from 'utils/bigNumber'
import { getFullDisplayBalance } from 'utils/formatBalance'
import { requiresApproval } from 'utils/requiresApproval'
import EditNumbersModal from './EditNumbersModal'
import NumTicketsToBuyButton from './NumTicketsToBuyButton'
import { useTicketsReducer } from './useTicketsReducer'
import { useCallNoGasPrice } from 'hooks/useCallDefaultGasPrice'
import BogoIcon from 'views/Lottery/svgs/Bogo'

const StyledModal = styled(Modal)`
  ${({ theme }) => theme.mediaQueries.md} {
    width: 280px;
  }
  div {
    overflow-x: hidden;
  }
`

const ShortcutButtonsWrapper = styled(Flex)<{ isVisible: boolean }>`
  justify-content: space-between;
  margin-top: 8px;
  margin-bottom: 24px;
  display: ${({ isVisible }) => (isVisible ? 'flex' : 'none')};
`

const StyledBalanceContainer = styled(Flex)`
  height: 60px;
  border: 1px solid rgba(255, 255, 255, 0.20); 
  justify-content: center;
  align-items: center;
  border-radius: 10px;
  margin: 0px 20px;
  text-align: center;
  div {
    padding: 0px;
    border-bottom: none !important;
    div {
      div {
        input {
          font-size: 18px;
          text-align: center;
        }
      }
    }
  }
`

const BogoContainer = styled(Flex)`
  background: linear-gradient(180deg, rgba(23, 20, 39, 0.40) 0%, rgba(205, 99, 67, 0.40) 72.4%, rgba(228, 142, 80, 0.40) 100%); 
  border-bottom: 1px solid ${({ theme }) => theme.colors.cardBorder};
  height: 70px;
  padding: 15px 20px;
  justify-content: space-between;
  align-items: center;
  align-self: stretch; 
  width: calc(100% + 40px);
  margin-left: -20px;
  margin-top: -20px;
  margin-bottom: 30px;
`

interface BuyTicketsModalProps {
  onDismiss?: () => void
}

enum BuyingStage {
  BUY = 'Buy',
  EDIT = 'Edit',
}

const BuyTicketsModal: React.FC<React.PropsWithChildren<BuyTicketsModalProps>> = ({ onDismiss }) => {
  const { account } = useWeb3React()
  const { t } = useTranslation()
  const { theme } = useTheme()
  const {
    maxNumberTicketsPerBuyOrClaim,
    currentLotteryId,
    currentRound: {
      priceTicketInEth,
      discountDivisor,
      isBogoOn,
      userTickets: { tickets: userCurrentTickets },
    },
  } = useLottery()
  
  const ethNotWeiPrice = priceTicketInEth ? priceTicketInEth.dividedBy(new BigNumber(10).pow(18)) : BIG_ZERO
  const { callNoGasPrice } = useCallNoGasPrice()
  const [ticketsToBuy, setTicketsToBuy] = useState('1')
  const [actualTickets, setActualTickets] = useState(isBogoOn ? '2' : '1');
  const [discountValue, setDiscountValue] = useState('')
  const [totalCost, setTotalCost] = useState('')
  const [ticketCostBeforeDiscount, setTicketCostBeforeDiscount] = useState('')
  const [buyingStage, setBuyingStage] = useState<BuyingStage>(BuyingStage.BUY)
  const [maxPossibleTicketPurchase, setMaxPossibleTicketPurchase] = useState(BIG_ZERO)
  const [maxTicketPurchaseExceeded, setMaxTicketPurchaseExceeded] = useState(false)
  const [userNotEnoughCake, setUserNotEnoughCake] = useState(false)
  const lotteryContract = useNewLotteryV2Contract()
  const { toastSuccess } = useToast()
  const { balance, fetchStatus } = useGetBnbBalance()
  // balance from useTokenBalance causes rerenders in effects as a new BigNumber is instantiated on each render, hence memoising it using the stringified value below.
  const stringifiedUserCake = balance.toJSON()
  const memoisedUserCake = useMemo(() => new BigNumber(stringifiedUserCake), [stringifiedUserCake])

  const ethPrice = useEthPriceBusd()
  const dispatch = useAppDispatch()
  const hasFetchedBalance = fetchStatus === FetchStatus.Fetched

  const TooltipComponent = () => (
    <>
      <Text mb="16px">
        {t(
          'Buying multiple tickets in a single transaction gives a discount. The discount increases in a linear way, up to the maximum of 100 tickets:',
        )}
      </Text>
      <Text>{t('2 tickets: 0.05%')}</Text>
      <Text>{t('50 tickets: 2.45%')}</Text>
      <Text>{t('100 tickets: 4.95%')}</Text>
    </>
  )
  const { targetRef, tooltip, tooltipVisible } = useTooltip(<TooltipComponent />, {
    placement: 'bottom-end',
    tooltipOffset: [20, 10],
  })

  const limitNumberByMaxTicketsPerBuy = useCallback(
    (number: BigNumber) => {
      return number.gt(maxNumberTicketsPerBuyOrClaim) ? maxNumberTicketsPerBuyOrClaim : number
    },
    [maxNumberTicketsPerBuyOrClaim],
  )

  const getTicketCostAfterDiscount = useCallback(
    (numberTickets: BigNumber) => {
      const ticketsToPayFor = isBogoOn ? numberTickets.dividedBy(2) : numberTickets;
      
      const totalAfterDiscount = priceTicketInEth
        .times(ticketsToPayFor)
        .times(discountDivisor.plus(1).minus(ticketsToPayFor))
        .div(discountDivisor)
      return totalAfterDiscount
    },
    [discountDivisor, priceTicketInEth, isBogoOn],
)

  const getMaxTicketBuyWithDiscount = useCallback(
    (numberTickets: BigNumber) => {
      const costAfterDiscount = getTicketCostAfterDiscount(numberTickets)
      const costBeforeDiscount = ethNotWeiPrice.times(numberTickets)
      const discountAmount = costBeforeDiscount.minus(costAfterDiscount)
      const ticketsBoughtWithDiscount = discountAmount.div(ethNotWeiPrice)
      const overallTicketBuy = numberTickets.plus(ticketsBoughtWithDiscount)
      return { overallTicketBuy, ticketsBoughtWithDiscount }
    },
    [getTicketCostAfterDiscount, ethNotWeiPrice],
  )

  const validateInput = useCallback(
    (inputNumber: BigNumber) => {
      const ticketsAfterBogo = isBogoOn ? inputNumber.times(2) : inputNumber
      const limitedNumberTickets = limitNumberByMaxTicketsPerBuy(ticketsAfterBogo)
      const cakeCostAfterDiscount = getTicketCostAfterDiscount(limitedNumberTickets)
      if (cakeCostAfterDiscount.gt(new BigNumber(balance?.toString()))) {
        setUserNotEnoughCake(true)
      } else if ((isBogoOn ? inputNumber.times(2) : inputNumber).isGreaterThan(maxNumberTicketsPerBuyOrClaim)) {
        setMaxTicketPurchaseExceeded(true)
      } else {
        setUserNotEnoughCake(false)
        setMaxTicketPurchaseExceeded(false)
      }
    },
    [limitNumberByMaxTicketsPerBuy, getTicketCostAfterDiscount, maxNumberTicketsPerBuyOrClaim, balance],
  )

  useEffect(() => {
    const getMaxPossiblePurchase = () => {
      const maxBalancePurchase = memoisedUserCake.div(ethNotWeiPrice)
      const limitedMaxPurchase = limitNumberByMaxTicketsPerBuy(maxBalancePurchase)
      let maxPurchase

      // If the users' max ETH balance purchase is less than the contract limit - factor the discount logic into the max number of tickets they can purchase
      if (limitedMaxPurchase.lt(maxNumberTicketsPerBuyOrClaim)) {
        // Get max tickets purchasable with the users' balance, as well as using the discount to buy tickets
        const { overallTicketBuy: maxPlusDiscountTickets } = getMaxTicketBuyWithDiscount(limitedMaxPurchase)

        // Knowing how many tickets they can buy when counting the discount - plug that total in, and see how much that total will get discounted
        const { ticketsBoughtWithDiscount: secondTicketDiscountBuy } =
          getMaxTicketBuyWithDiscount(maxPlusDiscountTickets)

        // Add the additional tickets that can be bought with the discount, to the original max purchase
        maxPurchase = limitedMaxPurchase.plus(secondTicketDiscountBuy)
      } else {
        maxPurchase = limitedMaxPurchase
      }

      setMaxPossibleTicketPurchase(maxPurchase)
    }
    getMaxPossiblePurchase()
  }, [
    maxNumberTicketsPerBuyOrClaim,
    ethNotWeiPrice,
    memoisedUserCake,
    limitNumberByMaxTicketsPerBuy,
    getTicketCostAfterDiscount,
    getMaxTicketBuyWithDiscount,
    hasFetchedBalance,
  ])

  useEffect(() => {
    const numberOfTicketsToBuy = new BigNumber(ticketsToBuy)
    const costAfterDiscount = getTicketCostAfterDiscount(numberOfTicketsToBuy)
    const costBeforeDiscount = priceTicketInEth.times(numberOfTicketsToBuy)
    const discountBeingApplied = costBeforeDiscount.minus(costAfterDiscount)
    setTicketCostBeforeDiscount(costBeforeDiscount.gt(0) ? getFullDisplayBalance(costBeforeDiscount) : '0')
    setTotalCost(costBeforeDiscount.gt(0) ? getFullDisplayBalance(costBeforeDiscount) : '0')
    setDiscountValue(discountBeingApplied.gt(0) ? getFullDisplayBalance(discountBeingApplied, 18, 5) : '0')
  }, [ticketsToBuy, ethNotWeiPrice, discountDivisor, getTicketCostAfterDiscount, priceTicketInEth, isBogoOn])

  const getNumTicketsByPercentage = (percentage: number): number => {
    const percentageOfMaxTickets = maxPossibleTicketPurchase.gt(0)
      ? maxPossibleTicketPurchase.div(new BigNumber(isBogoOn ? 50 : 100)).times(new BigNumber(percentage))
      : BIG_ZERO
    return Math.floor(percentageOfMaxTickets.toNumber())
  }

  const handleInputChange = (input: string) => {
    const inputAsInt = parseInt(input, 10)
    const inputAsBN = new BigNumber(inputAsInt)
    const limitedNumberTickets = limitNumberByMaxTicketsPerBuy(inputAsBN)
    validateInput(inputAsBN)
    setTicketsToBuy(inputAsInt ? limitedNumberTickets.toString() : '')
    const actualTicketCount = isBogoOn ? inputAsInt * 2 : inputAsInt;
    setActualTickets(actualTicketCount.toString());
  }

  const handleNumberButtonClick = (number: number) => {
    const finalTicketCount = isBogoOn ? number * 2 : number
    setTicketsToBuy(finalTicketCount.toFixed())
    setUserNotEnoughCake(false)
    setMaxTicketPurchaseExceeded(false)
  }

  const [updateTicket, randomize, tickets, allComplete, getTicketsForPurchase] = useTicketsReducer(
    parseInt(actualTickets, 10),
    userCurrentTickets,
  )

  const onConfirm = async() => {
    const ticketsForPurchase = getTicketsForPurchase()
    const ticketsToPayFor = isBogoOn ? ticketsForPurchase.length / 2 : ticketsForPurchase.length
    try {
      const txReceipt = await callNoGasPrice(
        lotteryContract, 
        'buyTickets', 
        [currentLotteryId, ticketsForPurchase], 
        { value: priceTicketInEth.times(ticketsToPayFor).plus(1).toString()})
      await txReceipt.wait()
      if (txReceipt) {
        onSuccess({ receipt: txReceipt });
      } else {
        console.error("Transaction failed:", txReceipt);
      }
    } catch (error) {
        console.error("Error while buying tickets:", error);
  }
}
  
  const onSuccess = async ({ receipt }) => {
    onDismiss?.()
    dispatch(fetchUserTicketsAndLotteries({ account, currentLotteryId }))
    toastSuccess(t('Lottery tickets purchased!'), <ToastDescriptionWithTx txHash={receipt.transactionHash} />)
  }

  const getErrorMessage = () => {
    if (userNotEnoughCake) return t('Insufficient ETH balance')
    return t('The maximum number of tickets you can buy in one transaction is %maxTickets%', {
      maxTickets: maxNumberTicketsPerBuyOrClaim.toString(),
    })
  }

  const percentageDiscount = () => {
    const percentageAsBn = new BigNumber(discountValue).div(new BigNumber(ticketCostBeforeDiscount)).times(100)
    if (percentageAsBn.isNaN() || percentageAsBn.eq(0)) {
      return 0
    }
    return percentageAsBn.toNumber().toFixed(2)
  }

  const disableBuying =
    userNotEnoughCake ||
    !ticketsToBuy ||
    maxTicketPurchaseExceeded ||
    new BigNumber(ticketsToBuy).lte(0) ||
    getTicketsForPurchase().length !== parseInt(actualTickets, 10)

  if (buyingStage === BuyingStage.EDIT) {
    return (
      <EditNumbersModal
        totalCost={totalCost}
        updateTicket={updateTicket}
        randomize={randomize}
        tickets={tickets}
        allComplete={allComplete}
        onConfirm={onConfirm}
        isConfirming={false}
        onDismiss={() => setBuyingStage(BuyingStage.BUY)}
      />
    )
  }

  return (
    <StyledModal title={t('Buy Tickets')} onDismiss={onDismiss} headerBackground={theme.colors.gradientCardHeader}>
      {tooltipVisible && tooltip}
      {isBogoOn && (
        <BogoContainer>
          <Flex flexDirection='column'>
            <Text color='red' fontSize='14px' fontWeight={500}>
              {'THIS WEEK ONLY'}
            </Text>
            <Text color='text' fontSize='18px'>
              {'Buy 1 ticket, get one free'}
            </Text>
          </Flex>
          <BogoIcon />
        </BogoContainer>
      )}
      <Flex alignItems='center' mb='15px' mx='auto'>
      <Button
          disabled={parseInt(actualTickets) === 0}
          onClick={() => handleInputChange((parseFloat(ticketsToBuy) - 1).toString())}
          variant="tertiary"
          scale="sm"
          style={{borderRadius: '100px', height: '44px', width: '44px'}}
        >
          <MinusIcon width="24px" color="text" />
        </Button>
        <StyledBalanceContainer>
          <BalanceInput
            isWarning={account && (userNotEnoughCake || maxTicketPurchaseExceeded)}
            placeholder="0"
            value={ticketsToBuy}
            onUserInput={handleInputChange}
            // currencyValue={
            //   ethPrice.gt(0) &&
            //   `~${ticketsToBuy ? getFullDisplayBalance(ethNotWeiPrice.times(new BigNumber(ticketsToBuy))) : '0.00'} ETH`
            // }
          />
        </StyledBalanceContainer>
        <Button
          disabled={actualTickets === '100'}
          onClick={() => handleInputChange((ticketsToBuy === '' ? 1 : parseInt(ticketsToBuy) + 1).toString())}
          variant="tertiary"
          scale="sm"
          style={{borderRadius: '100px', height: '44px', width: '44px'}}
        >
          <AddIcon width="24px" color="text" />
        </Button>
      </Flex>
      <ShortcutButtonsWrapper isVisible={account && hasFetchedBalance}>
            <NumTicketsToBuyButton onClick={() => handleInputChange('10')}>
              {hasFetchedBalance ? 10 : ``}
            </NumTicketsToBuyButton>
            <NumTicketsToBuyButton onClick={() => handleInputChange('25')}>
              {hasFetchedBalance ? 25 : ``}
            </NumTicketsToBuyButton>
            <NumTicketsToBuyButton onClick={() => handleInputChange('50')}>
              {hasFetchedBalance ? 50 : ``}
            </NumTicketsToBuyButton>
            <NumTicketsToBuyButton onClick={() => handleInputChange('50')}>
                {t('Max')}
            </NumTicketsToBuyButton>
        </ShortcutButtonsWrapper>
      {isBogoOn && (
        <Text color="success" fontSize="12px" mt="4px">
          BOGO Offer Active: You will receive {parseFloat(ticketsToBuy) * 2} tickets for the price of {ticketsToBuy}.
        </Text>
      )}
      <Flex borderTop={`1px solid ${theme.colors.cardBorder}`} alignItems="center" justifyContent="center" py='10px' width='100%' mt='30px'>
        <Flex justifyContent="space-between" flexDirection="column" width='100%'>
          {account && (userNotEnoughCake || maxTicketPurchaseExceeded) && (
            <Text fontSize="12px" color="red">
              {getErrorMessage()}
            </Text>
          )}
          {account && (
            <Flex justifyContent="space-between">
              <Text color="textSubtle" fontSize="14px">
              {t('Balance')}:
              </Text>
              {hasFetchedBalance ? (
                <Text fontSize="14px" color="text">
                  {parseFloat(balance.toString()) / 1e18}
                </Text>
              ) : (
                <Skeleton width={50} height={12} />
              )}
            </Flex>
          )}
        </Flex>
      </Flex>

      <Flex borderTop={`1px solid ${theme.colors.cardBorder}`} flexDirection="column" >
        <Flex justifyContent="space-between" py='10px'>
          <Text color="textSubtle" fontSize="14px">
            {t('Cost')} (ETH)
          </Text>
          <Text color="text" fontSize="14px">
            {priceTicketInEth && getFullDisplayBalance(priceTicketInEth.times(ticketsToBuy || 0))} ETH
          </Text>
        </Flex>
        {/* <Flex mb="8px" justifyContent="space-between">
          <Flex>
            <Text display="inline" bold fontSize="14px" mr="4px">
              {discountValue && totalCost ? percentageDiscount() : 0}%
            </Text>
            <Text display="inline" color="textSubtle" fontSize="14px">
              {t('Bulk discount')}
            </Text>
            <Flex alignItems="center" justifyContent="center" ref={targetRef}>
              <HelpIcon ml="4px" width="14px" height="14px" color="textSubtle" />
            </Flex>
          </Flex>
          <Text fontSize="14px" color="textSubtle">
            ~{discountValue} ETH
          </Text>
        </Flex> */}
        <Flex borderTop={`1px solid ${theme.colors.cardBorder}`} pt="8px" mb="24px" justifyContent="space-between">
          <Text color="textSubtle" fontSize="18px">
            {t('You pay')}
          </Text>
          <Text fontSize="18px">
            ~{totalCost?.slice(0, 12)} ETH
          </Text>
        </Flex>

        {account ? (
          <>
            <ApproveConfirmButtons
              isConfirmDisabled={disableBuying}
              isApproveDisabled
              onConfirm={onConfirm}
              buttonArrangement={ButtonArrangement.SEQUENTIAL}
              confirmLabel={t('Buy Instantly')}
              confirmId="lotteryBuyInstant"
            />
            <Button
              variant="secondary"
              mt="8px"
              disabled={disableBuying}
              onClick={() => {
                setBuyingStage(BuyingStage.EDIT)
              }}
            >
              {t('View/Edit Numbers')}
            </Button>
          </>
        ) : (
          <ConnectWalletButton />
        )}

        <Text mt="24px" fontSize="12px" color="textSubtle">
          {t(
            '"Buy Instantly" chooses random numbers, with no duplicates among your tickets. Prices are set before each round starts. Purchases are final.',
          )}
        </Text>
      </Flex>
    </StyledModal>
  )
}

export default BuyTicketsModal
